import React from 'react';
import type { PageFC } from '@salesforce/pwa-kit-react-sdk';
import { BUILD_INFO } from '../../utils/build-info';
import { isServer } from '../../utils/utils';
import { useBuildStatus } from './hooks/useBuildStatus';

type Props = {
    BRANCH?: string;
    DATE_TIME?: string;
    COMMIT?: string;
    VERSION?: string;
};

const StatusIndicator = (): React.ReactElement => (
    <span className="bg-green-500 mr-2 inline-block h-2 w-2 rounded-full" />
);

const ServiceName = ({ name }: { name: string }): React.ReactElement => (
    <span className="text-gray-800 font-semibold">{name}</span>
);

const LinkCell = ({
    href,
    children,
}: {
    href: string;
    children: React.ReactNode;
}): React.ReactElement => (
    <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="text-blue-600 hover:text-blue-800 font-medium"
    >
        {children}
    </a>
);

const VersionCell = ({ version }: { version: string }): React.ReactElement => (
    <span className="bg-gray-100 rounded px-2 py-1 font-mono text-xs">{version}</span>
);

const CommitCell = ({ commit, href }: { commit: string; href: string }): React.ReactElement => (
    <LinkCell href={href}>
        <span className="text-gray-600 font-mono text-xs">{commit}</span>
    </LinkCell>
);

const DateCell = ({ date }: { date: string }): React.ReactElement => (
    <span className="text-gray-600 text-xs">{date}</span>
);

const BuildStatus: PageFC<Props> = ({ BRANCH, COMMIT, DATE_TIME, VERSION }: Props) => {
    const { data, isLoading } = useBuildStatus();

    if (isLoading) {
        return null;
    }

    return (
        <main className="bg-gray-50 min-h-screen p-8">
            <div className="mx-auto max-w-6xl">
                <div className="mb-8 text-center">
                    <h1 className="text-gray-800 mb-2 text-3xl font-semibold">SFCC Build Status</h1>
                </div>

                <div className="border-gray-200 overflow-hidden rounded-lg border bg-white shadow-sm">
                    <div className="bg-gray-50 border-gray-200 border-b px-6 py-4">
                        <h2 className="text-gray-700 text-sm font-medium uppercase tracking-wide">
                            Service Status Overview
                        </h2>
                    </div>

                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead>
                                <tr className="bg-gray-50">
                                    <th className="text-gray-700 border-gray-200 border-b px-6 py-3 text-left text-sm font-medium">
                                        Service
                                    </th>
                                    <th className="text-gray-700 border-gray-200 border-b px-6 py-3 text-left text-sm font-medium">
                                        Repository
                                    </th>
                                    <th className="text-gray-700 border-gray-200 border-b px-6 py-3 text-left text-sm font-medium">
                                        Branch
                                    </th>
                                    <th className="text-gray-700 border-gray-200 border-b px-6 py-3 text-left text-sm font-medium">
                                        Commit
                                    </th>
                                    <th className="text-gray-700 border-gray-200 border-b px-6 py-3 text-left text-sm font-medium">
                                        Version
                                    </th>
                                    <th className="text-gray-700 border-gray-200 border-b px-6 py-3 text-left text-sm font-medium">
                                        Build Date
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr className="hover:bg-gray-50 border-gray-100 border-b">
                                    <td className="px-6 py-4">
                                        <StatusIndicator />
                                        <ServiceName name="PWA" />
                                    </td>
                                    <td className="px-6 py-4">
                                        <LinkCell href="https://github.com/sur-la-table/slt-sfcc-react">
                                            slt-sfcc-react
                                        </LinkCell>
                                    </td>
                                    <td
                                        className="px-6 py-4"
                                        data-testid="build-status-page-pwa-branch"
                                    >
                                        <LinkCell
                                            href={`https://github.com/sur-la-table/slt-sfcc-react/tree/${
                                                BRANCH || 'main'
                                            }`}
                                        >
                                            {BRANCH || 'main'}
                                        </LinkCell>
                                    </td>
                                    <td
                                        className="px-6 py-4"
                                        data-testid="build-status-page-pwa-commit"
                                    >
                                        {COMMIT ? (
                                            <CommitCell
                                                commit={COMMIT}
                                                href={`https://github.com/sur-la-table/slt-sfcc-react/commit/${COMMIT}`}
                                            />
                                        ) : (
                                            <span className="text-gray-400 text-xs">-</span>
                                        )}
                                    </td>
                                    <td
                                        className="px-6 py-4"
                                        data-testid="build-status-page-pwa-version"
                                    >
                                        <VersionCell version={VERSION || '1.2.3'} />
                                    </td>
                                    <td
                                        className="px-6 py-4"
                                        data-testid="build-status-page-pwa-date"
                                    >
                                        <DateCell date={DATE_TIME || '2024-01-15 14:30:25'} />
                                    </td>
                                </tr>

                                <tr className="hover:bg-gray-50 border-gray-100 border-b">
                                    <td className="px-6 py-4">
                                        <StatusIndicator />
                                        <ServiceName name="BFF" />
                                    </td>
                                    <td className="px-6 py-4">
                                        <LinkCell href="https://github.com/sur-la-table/slt-sfcc-bff">
                                            slt-sfcc-bff
                                        </LinkCell>
                                    </td>
                                    <td
                                        className="px-6 py-4"
                                        data-testid="build-status-page-bff-branch"
                                    >
                                        <LinkCell
                                            href={`https://github.com/sur-la-table/slt-sfcc-bff/tree/${
                                                data?.bff.BRANCH || 'develop'
                                            }`}
                                        >
                                            {data?.bff.BRANCH || 'develop'}
                                        </LinkCell>
                                    </td>
                                    <td
                                        className="px-6 py-4"
                                        data-testid="build-status-page-bff-commit"
                                    >
                                        {data?.bff.COMMIT ? (
                                            <CommitCell
                                                commit={data.bff.COMMIT}
                                                href={`https://github.com/sur-la-table/slt-sfcc-bff/commit/${data.bff.COMMIT}`}
                                            />
                                        ) : (
                                            <span className="text-gray-400 text-xs">-</span>
                                        )}
                                    </td>
                                    <td
                                        className="px-6 py-4"
                                        data-testid="build-status-page-bff-version"
                                    >
                                        <VersionCell version={data?.bff.VERSION || '2.1.0'} />
                                    </td>
                                    <td
                                        className="px-6 py-4"
                                        data-testid="build-status-page-bff-date"
                                    >
                                        <DateCell
                                            date={data?.bff.DATE_TIME || '2024-01-15 13:45:12'}
                                        />
                                    </td>
                                </tr>

                                <tr className="hover:bg-gray-50 border-gray-100 border-b">
                                    <td className="px-6 py-4">
                                        <StatusIndicator />
                                        <ServiceName name="Site Genesis" />
                                    </td>
                                    <td className="px-6 py-4">
                                        <LinkCell href="https://github.com/sur-la-table/slt-sfcc">
                                            slt-sfcc
                                        </LinkCell>
                                    </td>
                                    <td
                                        className="px-6 py-4"
                                        data-testid="build-status-page-sfcc-branch"
                                    >
                                        <LinkCell
                                            href={`https://github.com/sur-la-table/slt-sfcc/tree/${
                                                data?.sfcc.BRANCH || 'master'
                                            }`}
                                        >
                                            {data?.sfcc.BRANCH || 'master'}
                                        </LinkCell>
                                    </td>
                                    <td
                                        className="px-6 py-4"
                                        data-testid="build-status-page-sfcc-commit"
                                    >
                                        {data?.sfcc.COMMIT ? (
                                            <CommitCell
                                                commit={data.sfcc.COMMIT}
                                                href={`https://github.com/sur-la-table/slt-sfcc/commit/${data.sfcc.COMMIT}`}
                                            />
                                        ) : (
                                            <span className="text-gray-400 text-xs">-</span>
                                        )}
                                    </td>
                                    <td
                                        className="px-6 py-4"
                                        data-testid="build-status-page-sfcc-version"
                                    >
                                        <VersionCell version={data?.sfcc.VERSION || '3.0.1'} />
                                    </td>
                                    <td
                                        className="px-6 py-4"
                                        data-testid="build-status-page-sfcc-date"
                                    >
                                        <DateCell
                                            date={data?.sfcc.DATE_TIME || '2024-01-15 12:20:45'}
                                        />
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </main>
    );
};

BuildStatus.defaultProps = {};

BuildStatus.shouldGetProps = (): Promise<boolean> => Promise.resolve(isServer);

BuildStatus.getProps = (): Promise<Props> => Promise.resolve(BUILD_INFO);

export { BuildStatus };
